import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectIsMobileViewport } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId } from '@repo/store/maps/slices/mapData';
import type { VideoPlaybackEvent } from '@repo/ui/video/video-player/video-player-types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { LazyVideoPlayer as VideoPlayer } from './lazy-video-player';
import { type MediaItem, PinMediaLightbox } from './pin-media-lightbox';
const baseContainerStyles = css({
  display: 'flex',
  width: 'full',
  borderRadius: '16',
  overflow: 'hidden',
  background: 'transparent',
  border: 'none',
  outline: '[0]',
  padding: '0',
  flex: '[0 0 auto]',
  position: 'relative'
});
const shimmerOverlayStyles = css({
  position: 'absolute',
  inset: 'none',
  zIndex: '[1]',
  background: '[rgba(255, 255, 255, 0.06)]',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 'none',
    background: '[linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.08) 50%, transparent 100%)]',
    animation: '[shimmer 1.5s ease-in-out infinite]'
  }
});
const mediaButtonStyles = css({
  position: 'relative',
  borderRadius: '16',
  cursor: 'pointer',
  width: 'full',
  height: 'full',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 'none',
    left: 'none',
    right: 'none',
    height: 'full',
    background: '[linear-gradient(#00000033 0%, #0000 35%, #0000 70%, #00000033 100%)]',
    borderRadius: '16',
    pointerEvents: 'none',
    zIndex: '[1]'
  },
  '& img, & video': {
    opacity: '[1]',
    transition: '[opacity 0.15s]',
    margin: '[0 auto]',
    width: 'full',
    borderRadius: '16',
    aspectRatio: '[16 / 9]',
    objectFit: 'cover'
  },
  '&:hover img, &:hover video': {
    opacity: '[0.9]'
  }
});
const heroStyles = css({
  borderRadius: 'none',
  height: '[140px]',
  overflow: 'visible',
  '& button': {
    borderRadius: 'none',
    overflow: 'hidden',
    maxHeight: '[140px]',
    '&::after': {
      borderRadius: 'none'
    }
  },
  '& .video-js': {
    maxHeight: '[140px]',
    overflow: 'hidden'
  },
  '& .video-js video': {
    height: '[140px !important]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '& img, & video': {
    borderRadius: 'none',
    height: '[140px]',
    aspectRatio: '[auto]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '&:hover img, &:hover video': {
    opacity: '[1]'
  }
});
const compactHeroStyles = css({
  borderRadius: 'none',
  height: '[120px]',
  overflow: 'visible',
  '& button': {
    borderRadius: 'none',
    overflow: 'hidden',
    maxHeight: '[120px]',
    '&::after': {
      borderRadius: 'none'
    }
  },
  '& .video-js': {
    maxHeight: '[120px]',
    overflow: 'hidden'
  },
  '& .video-js video': {
    height: '[120px !important]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '& img, & video': {
    borderRadius: 'none',
    height: '[120px]',
    aspectRatio: '[auto]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '&:hover img, &:hover video': {
    opacity: '[1]'
  }
});

/** ~25% taller than default hero — used by hotspot map panes. */
const tallHeroStyles = css({
  borderRadius: 'none',
  height: '[175px]',
  overflow: 'visible',
  '& button': {
    borderRadius: 'none',
    overflow: 'hidden',
    maxHeight: '[175px]',
    '&::after': {
      borderRadius: 'none'
    }
  },
  '& .video-js': {
    maxHeight: '[175px]',
    overflow: 'hidden'
  },
  '& .video-js video': {
    height: '[175px !important]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '& img, & video': {
    borderRadius: 'none',
    height: '[175px]',
    aspectRatio: '[auto]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '&:hover img, &:hover video': {
    opacity: '[1]'
  }
});
const tallCompactHeroStyles = css({
  borderRadius: 'none',
  height: '[150px]',
  overflow: 'visible',
  '& button': {
    borderRadius: 'none',
    overflow: 'hidden',
    maxHeight: '[150px]',
    '&::after': {
      borderRadius: 'none'
    }
  },
  '& .video-js': {
    maxHeight: '[150px]',
    overflow: 'hidden'
  },
  '& .video-js video': {
    height: '[150px !important]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '& img, & video': {
    borderRadius: 'none',
    height: '[150px]',
    aspectRatio: '[auto]',
    objectFit: 'cover',
    objectPosition: 'center'
  },
  '&:hover img, &:hover video': {
    opacity: '[1]'
  }
});
const hoverOverlayStyles = css({
  position: 'absolute',
  inset: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '8',
  background: '[linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.4))]',
  opacity: '[0]',
  transition: '[opacity 0.2s ease]',
  pointerEvents: 'none',
  'button:hover > &': {
    opacity: '[1]'
  }
});
const hoverContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8'
});
const nonHeroOverlayStyles = css({
  position: 'absolute',
  bottom: '[0px]',
  width: 'full',
  padding: '8',
  display: 'flex',
  pointerEvents: 'none'
});
const nonHeroLabelStyles = css({
  appearance: 'none',
  border: 'none',
  outline: 'none',
  display: 'flex',
  padding: '8',
  background: '[rgba(19, 20, 23, 0.25)]',
  backdropFilter: '[blur(17.5px)]',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'white',
  borderRadius: 'full',
  gap: '8',
  fontSize: 'md',
  fontWeight: 'medium',
  lineHeight: '[18px]',
  letterSpacing: '[-0.25px]',
  pointerEvents: 'none',
  '& i': {
    color: 'white'
  }
});
export interface PinMediaProps {
  imagePath?: string | null;
  imageId?: number | null;
  videoPath?: string | null;
  title?: string;
  hero?: boolean;
  /** ~25% taller hero media (hotspot panes). Only applies when `hero` is true. */
  tallHero?: boolean;
  compact?: boolean;
  lightboxOpen?: boolean;
  onLightboxChange?: (open: boolean) => void;
}
export const PinMedia = ({
  imagePath,
  imageId,
  videoPath,
  title,
  hero = false,
  tallHero = false,
  compact = false,
  lightboxOpen,
  onLightboxChange
}: PinMediaProps) => {
  const {
    t
  } = useT(['game_map']);
  const [mediaError, setMediaError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [internalLightboxOpen, setInternalLightboxOpen] = useState(false);
  const isLightboxOpen = lightboxOpen !== undefined ? lightboxOpen : internalLightboxOpen;
  const setIsLightboxOpen = (open: boolean) => {
    if (onLightboxChange) {
      onLightboxChange(open);
    } else {
      setInternalLightboxOpen(open);
    }
  };

  // Reset loading state when media source changes (e.g. switching pins)
  const mediaKey = `${imageId}-${imagePath}-${videoPath}`;
  const prevMediaKeyRef = useRef(mediaKey);
  if (prevMediaKeyRef.current !== mediaKey) {
    prevMediaKeyRef.current = mediaKey;
    setLoaded(false);
    setMediaError(false);
    setVideoReady(false);
  }
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMedia = imageId || imagePath || videoPath;
  const externalMapId = useAppSelector(selectExternalMapId);
  const isMobileViewport = useAppSelector(selectIsMobileViewport);
  const hostType = useHostType();
  const analytics = useAnalytics();
  const handleMediaLoad = useCallback(() => {
    setLoaded(true);
  }, []);
  const handleMediaError = useCallback(() => {
    setMediaError(true);
  }, []);

  // The player switches sources in place (e.g. stepping through heatmap clips), so readiness has
  // to be restored per source after the media-key reset above, not once per player.
  const handlePlaybackEvent = useCallback((event: VideoPlaybackEvent) => {
    if (event.eventType === 'loadedmetadata') {
      setVideoReady(true);
      setLoaded(true);
    } else if (event.eventType === 'error') {
      setMediaError(true);
    }
  }, []);
  const handleMediaClick = () => {
    setIsLightboxOpen(true);
    if (analytics && externalMapId && hostType) {
      const imageIds = imageId ? [imageId] : [];
      const videoPaths = videoReady && videoPath ? [videoPath] : [];
      analytics.event('game_map_open_pin_media_lightbox', {
        mapId: externalMapId,
        imageIds,
        videoPaths,
        platform: hostType
      });
    }
  };
  const getImagePath = useCallback((size: number) => {
    if (imageId) {
      return `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_pin/${imageId}/${size}.webp`;
    }
    if (imagePath) {
      return imagePath;
    }
    return null;
  }, [imageId, imagePath]);
  const videoSourceUrl = videoPath?.startsWith('https://') ? videoPath : `https://${videoPath}`;
  const mediaItems = useMemo((): MediaItem[] => {
    const items: MediaItem[] = [];
    if (videoPath && videoReady) {
      items.push({
        type: 'video',
        src: videoSourceUrl
      });
    }
    if (imageId || imagePath) {
      items.push({
        type: 'image',
        src: getImagePath(1920) || '',
        alt: 'Location image'
      });
    }
    return items;
  }, [imageId, imagePath, videoPath, videoReady, videoSourceUrl, getImagePath]);
  const heroClassName = tallHero ? compact ? tallCompactHeroStyles : tallHeroStyles : compact ? compactHeroStyles : heroStyles;
  const containerClassName = `${baseContainerStyles} ${hero ? heroClassName : ''}`;
  return <>
            <div ref={containerRef} className={containerClassName}>
                {!loaded && hasMedia && <div className={shimmerOverlayStyles} />}
                <div className={css({
        position: 'relative',
        width: 'full'
      })}>
                    <button type="button" className={mediaButtonStyles} onClick={handleMediaClick} aria-label={title ? `View ${title} media` : 'View location media'}>
                        {(imageId || imagePath) && !videoPath && !mediaError && <img ref={imageRef} src={getImagePath(750) || ''} alt="Location media" onLoad={handleMediaLoad} onError={handleMediaError} style={{
            opacity: loaded ? 1 : 0
          }} />}

                        {videoPath && <VideoPlayer src={videoPath} onPlaybackEvent={handlePlaybackEvent}
          /* Hero: autoplay once to draw attention, then rest on last frame with hover overlay */ autoPlay={hero} playOnHover={!isMobileViewport} controls={false} muted={true} loop={!hero} />}

                        {loaded && hero && !isMobileViewport && <div className={hoverOverlayStyles}>
                                <div className={hoverContentStyles}>
                                    {videoPath && <Icon icon="play_arrow" size="md" fill className={css({
                color: 'white',
                marginRight: '-8'
              })} decorative />}
                                    <Type variant="caption" color="white">
                                        {t($ => $.click_to_view)}
                                    </Type>
                                </div>
                            </div>}
                        {loaded && !hero && videoPath && videoReady && !isMobileViewport && <div className={nonHeroOverlayStyles}>
                                <span className={nonHeroLabelStyles}>
                                    <Icon icon="mouse" size="md" decorative />
                                    {t($_0 => $_0.hover_for_video)}
                                </span>
                            </div>}
                    </button>

                    {loaded && !hero && <IconButton onClick={e => {
          e.stopPropagation();
          setIsLightboxOpen(true);
        }} color="secondary" size="sm" aria-label={t($_1 => $_1.expand)} className={css({
          position: 'absolute',
          right: '8',
          bottom: '8',
          pointerEvents: 'auto'
        })}>
                            <Icon icon="open_in_full" decorative />
                        </IconButton>}
                </div>
            </div>

            <PinMediaLightbox isOpen={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} mediaItems={mediaItems} initialIndex={0} title={title} />
        </>;
};